<template>
  <main class="report-patient-return-rate">
    <header class="report-patient-return-rate__header">
      <h1 class="report-patient-return-rate__title">Возвращаемость пациентов</h1>
      <form
        class="report-patient-return-rate__units"
        v-on:submit.prevent="onSubmit"
      >
        <div class="report-patient-return-rate__unit">
          <div
            class="report-patient-return-rate__group"
            v-if="hasFetchedOptions"
          >
            <date-range-with-presets
              v-bind:presets="periodPresets"
              v-model="periodDates"
              custom-color-date="black"
              v-on:input-preset="onChangePeriodType" />
            <span class="report-patient-return-rate__switch-wrapper">
              <v-switch
                v-model="reportInPercentage"
                class="switch-result"
              />
              Выводить в процентах
            </span>
          </div>
        </div>
        <div class="report-patient-return-rate__unit">
          <h2>Параметры фильтрации</h2>
          <div class="report-patient-return-rate__group">
            <multiple-autocomplete-input
              label="Врачи"
              removal-per-chip
              v-bind:items="employees"
              v-model="selectedEmployees" />
          </div>
        </div>
        <div class="report-patient-return-rate__print">
          <button
            v-if="id"
            type="button"
            v-on:click="fetchReportSpreadsheet"
            class="report-patient-return-rate__button"
          >EXCEL</button>
        </div>
      </form>
    </header>
    <section class="report-patient-return-rate__body">
      <table
        class="report-patient-return-rate__table"
        v-if="id">
        <thead class="report-patient-return-rate__table__header">
          <tr>
            <th
              class="report-patient-return-rate__table__header__cell"
              v-for="(column, index) in columns"
              v-bind:key="`column-${index}`"
            >
              <template>{{ column }}</template>
            </th>
          </tr>
        </thead>
        <tbody class="report-patient-return-rate__table__body">
          <template
            v-for="(fields, key) in lines">
            <tr
              class="report-patient-return-rate__table__body__line"
              v-bind:key="`line-${key}`">
              <td
                class="report-patient-return-rate__table__body__cell"
                v-for="(title, fieldIndex) in fields"
                v-bind:key="`${key}:${fieldIndex}`">
                {{ title }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <em v-else-if="isFetchingReport">Идет построение отчета...</em>
    </section>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import { convertToInputDate } from '@/utils/timeFormat';
import notifierMixin from '@/mixins/notifierMixin';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

export default {
  computed: {
    /**
     * @description Предварительные установки периодов.
     * @returns {{toValue: function(): [Date,Date], id: string, label: string, title: string}[]}
     */
    periodPresets() {
      return this.periodTypes.map(({ periodDates, title, value }) => ({
        id: value,
        label: title,
        title,
        toValue: () => periodDates,
      }));
    },
    /**
     * @description Выбранные даты периода.
     * @return {string}
     */
    selectedPeriod() {
      return this.periodDates.map(convertToInputDate).sort().join(',');
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this
          .periodTypes
          .slice(2, 3)
          .forEach(({ periodDates, value }) => {
            this.periodDates = periodDates;
            this.selectedPeriodType = value;
          });
      })
      .finally(() => {
        this.$nextTick(() => {
          this.hasFetchedOptions = true;
          this.fetchReport();
        });
      });
  },
  data() {
    return {
      columns: [],
      employees: [],
      expandedLines: [],
      hasFetchedOptions: false,
      id: null,
      isFetchingReport: false,
      lines: [],
      periodDates: [],
      periodTypes: [],
      selectedEmployees: [],
      selectedPeriodType: '',
      reportInPercentage: false,
    };
  },
  methods: {
    fetchReport() {
      this.isFetchingReport = true;
      this
        .$reportGateway
        .getPatientReturnRateReport(
          this.selectedEmployees,
          this.selectedPeriod,
          this.selectedPeriodType,
          this.reportInPercentage,
        )
        .then((result) => {
          result.forEach(({ columns, id, lines }) => {
            this.columns = columns;
            this.id = id;
            this.lines = lines;
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        })
        .finally(() => { this.isFetchingReport = false; });
    },
    fetchReportSpreadsheet() {
      const onSuccess = (response) => {
        saveFile(response, 'Возвращаемость пациентов.xls');
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$reportGateway
        .getPatientReturnRateReportBySheet(this.id)
        .then((result) => result.fold(onFail, onSuccess));
    },
    fetchOptions() {
      return this
        .$reportGateway
        .getPatientReturnRateReportOptions()
        .then((result) => {
          result.forEach((options) => {
            this.bases = options.bases;
            this.employees = options.employees;
            this.periodTypes = options
              .period_types
              .map(({ from_to: [from, to], title, value }) => ({
                periodDates: [new Date(from), new Date(to)],
                title,
                value,
              }));
          });
        });
    },
    onChangePeriodType(type) {
      this.selectedPeriodType = type;
    },
  },
  name: 'ClinicPanelReportPatientReturnRate',
  mixins: {
    notifierMixin,
  },
  components: {
    MultipleAutocompleteInput,
    DateRangeWithPresets,
  },
  watch: {
    selectedEmployees: {
      handler() {
        if (this.hasFetchedOptions) {
          this.fetchReport();
        }
      },
      immediate: true,
    },
    selectedPeriod: {
      handler() {
        if (this.hasFetchedOptions) {
          this.fetchReport();
        }
      },
      immediate: true,
    },
    selectedPeriodType: {
      handler() {
        if (this.hasFetchedOptions) {
          this.fetchReport();
        }
      },
      immediate: true,
    },
    reportInPercentage: {
      handler() {
        if (this.hasFetchedOptions) {
          this.fetchReport();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.report-patient-return-rate {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__units {
    background: #F2F5F8;
    max-width: 1283px;
  }
  &__unit {
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding: 20px 0 30px;
    border-top: 1px solid rgba(194, 207, 224, 0.50);
    h2 {
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      font-family: "Lato", sans-serif;
    }
    &:first-child {
      border-top: none;
    }
    &:nth-child(2) {
      gap: 0px;
    }
  }
  &__group {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    column-gap: 40px;
    row-gap: 20px;
  }
  &__print {
    display: flex;
    flex-flow: row wrap;
    align-items: baseline;
    column-gap: 20px;
  }
  &__input {
    max-width: 270px;
    width: 100%;
  }
  &__switch-wrapper {
    display: flex;
    align-items: center;
    align-self: center;
    column-gap: 15px;
    .switch-result {
      padding: 0;
      margin: 0;
      .v-input__slot {
        margin-bottom: 0;
      }
      .v-messages {
        display: none;
      }
    }
  }
  &__submit {
    @include buttons.btn-blue(210px, 48px, 16px);
    margin: 20px 0 0 0;
  }
  &__button {
    @include buttons.btn-transparent(210px, 48px, 16px);
    margin: 20px 0 0 0;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
  &__body {
    margin: 85px 0 0 0;
    padding: 0 20px 100px 23px;
  }
  &__table {
    border-collapse: collapse;
    font-size: 14px;
    line-height: 16px;
    overflow: auto;
    width: 100%;
    &__header {
      border-bottom: 2px solid #0489E0;
      font-weight: 700;
      &__cell {
        padding: 10px 8px;
        text-align: left;
      }
    }
    &__body {
      &__cell {
        padding: 15px 8px;
        text-align: left;
        white-space: nowrap;
        &:first-child {
          white-space: pre-wrap;
        }
      }
      &__line {
        border-bottom: 1px solid #EAEFF3;
        &:first-child {
          text-transform: uppercase;
          letter-spacing: 0.14px;
        }
      }
    }
    &__expansion {
      position: relative;
      &>input {
        position: absolute;
        z-index: -1;
        opacity: 0;
        &:checked+span {
          transform: rotate(180deg);
          background-position: top;
        }
      }
      &>span {
        display: inline-flex;
        align-items: center;
        user-select: none;
        cursor: pointer;
        background-image: url("@/assets/img/checked/arrow-top-blue.svg");
        background-size: contain;
        background-position: bottom;
        width: 15px;
        height: 15px;
        margin: 0 17px 0 0;
      }
    }
    &__sort {
      cursor: pointer;
      &:after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      & input {
        display: none;
      }
    }
  }
}
@media screen and (max-width: 800px) {
  .report-patient-return-rate {
    &__body {
      overflow: scroll;
    }
    &__table {
      width: 800px;
    }
  }
}
@media screen and (max-width: 600px) {
  .report-patient-return-rate {
    &__body {
      margin: 40px 0 0 0;
    }
    &__group {
      flex-direction: column;
      align-items: flex-start;
    }
    &__switch-wrapper {
      align-self: flex-start;
    }
    &__input {
      max-width: 100%;
    }
    &__group {
      .v-input:not(.switch-result) {
        width: 100%;
      }
      label.select {
        width: 100%;
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .report-patient-return-rate {
    &__header {
      padding: 40px 15px;
    }
    &__body {
      padding: 22px 15px 50px;
    }
  }
}
</style>
